/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/redux/store';
import { v4 as uuidv4 } from 'uuid';

import { getToken, logoutSession } from 'src/helpers/rest';
import ConversationComponent from 'src/components/molecules/conversation';
import { OpenAiResponse, Sources } from 'src/interfaces';
import LandingComponent from 'src/components/molecules/landing_component';

import API from 'src/apis';
import { setLoading, setTextQuery, setThreadFocus, setOpenAIResponse, setOpenAIPDFResponse, setPdfData } from 'src/redux/slices/open_ai';
import { serVersion, setSocketConnection } from 'src/redux/slices/socket';
import { OPEN_AI_STATUSES, BASE } from 'src/common/constants';

import styles from './index.module.scss';
import { message, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';

const ChatWrapper: React.FunctionComponent = () => {

    const dispatch = useDispatch();
    const {
        open_ai: { textQuery, threadFocus, openAIResponse },
        socket: { socketConnection }
    } = useAppSelector(state => state);
    const navigate = useNavigate();

    const initConnection = async () => {
        try {
            const credit = await checkForCredits();
            if (true) {
                showNoCreditPopUp();
                dispatch(setLoading(false));
                return;
            }
            if (socketConnection) return;

            let retryInit = false;
            let retryCount = 0;
            const MAX_RETRIES = 3;
            const RETRY_DELAY = 2000; // 2 seconds delay between retries

            const connectWebSocket = () => {
                try {
                    const token = getToken();
                    const socket = new WebSocket(BASE.WEBSCOKET_HOST + `/${window.location.pathname.includes('v1') ? 'llamabot' : 'pubmedbot_local'}/?token=${token}`);

                    socket.onopen = () => {
                        console.log('WebSocket connection opened');
                        dispatch(setSocketConnection(socket));
                        retryCount = 0;
                    };

                    socket.onerror = (error) => {
                        console.log('WebSocket error: ', error);
                        retryInit = true;
                        handleConnectionError();
                    };

                    socket.onclose = (event) => {
                        console.log('WebSocket connection closed', JSON.stringify(event));
                        if ((!event.wasClean || event?.reason === 'keepalive ping timeout') && retryCount < MAX_RETRIES && !retryInit) {
                            handleConnectionError();
                        }
                    };

                    let data = '';
                    let sources: Sources | null = null;
                    let loading_text = '';

                    socket.onmessage = (event) => {
                        const newData = JSON.parse(event.data);
                        if (newData?.status_code === 401) {
                            console.log('invalid or expired jwt');
                            logoutSession();
                            return;
                        }
                        if (OPEN_AI_STATUSES.includes(newData['status']) || (window.location.pathname.includes('v1') && newData['stream_status'] === 'started')) {
                            data = '';
                            sources = null;
                            loading_text = '';
                            if (newData['articleurl']) {
                                sources = { 'ArticleTitle': newData['articleurl'] };
                                loading_text = 'Analyzing sources... Almost there!';
                            }
                        }
                        if (newData['text_chunk']) {
                            data = data + newData['text_chunk'] + ' ';
                            loading_text = '';
                        }
                        dispatch(setOpenAIResponse((prev: OpenAiResponse[]) => {
                            const updatedResponses = [...prev];
                            if (updatedResponses.length > 0) {
                                updatedResponses[updatedResponses.length - 1] = {
                                    ...updatedResponses[updatedResponses.length - 1],
                                    answer: data.replace(/\n/g, '<br>'),
                                    sources: sources,
                                    loading_text: loading_text
                                };
                            }
                            return updatedResponses;
                        }));
                        if (newData['stream_status'] === 'end') {
                            dispatch(setLoading(false));
                            dispatch(setTextQuery(''));
                            dispatch(setOpenAIResponse((prev: OpenAiResponse[]) => {
                                const updatedResponses = [...prev];
                                if (updatedResponses.length > 0) {
                                    updatedResponses[updatedResponses.length - 1] = {
                                        ...updatedResponses[updatedResponses.length - 1],
                                        answer: data.replace(/\n/g, '<br>'),
                                        sources: sources,
                                        loader: false,
                                        status: newData['status'] || 'success'
                                    };
                                }
                                return updatedResponses;
                            }));
                            // 🔔 Send message to parent window
                            window?.parent?.postMessage(
                                { type: 'CHAT_STREAM_COMPLETED', status: 'success' },
                                '*'
                            );
                        }
                    };
                } catch (error) {
                    console.log('Error creating WebSocket connection:', error);
                    handleConnectionError();
                }
            };

            const handleConnectionError = () => {
                if (retryCount < MAX_RETRIES) {
                    retryCount++;
                    console.log(`Retrying connection attempt ${retryCount} of ${MAX_RETRIES}`);
                    setTimeout(() => {
                        connectWebSocket();
                    }, RETRY_DELAY);
                } else {
                    console.log('Max retry attempts reached');
                    dispatch(setSocketConnection(null));
                    dispatch(setLoading(false));
                    message.error("Unable to establish connection. Please try again in a while. If the issue persists, contact support.");
                }
            };

            return connectWebSocket();

        } catch (error) {
            console.log(error);
        }
    }

    const checkForCredits = async () => {
        try {
            const { status, credit } = await API.PingCredit();
            if (status !== 'success') {
                throw new Error('Failed to fetch credits');
            }
            if (credit <= 0) {
                if (socketConnection) {
                    socketConnection.close();
                    setSocketConnection(null);
                }
            }
            return credit;
        } catch (error) {
            console.log(error);
            return 0;
        }
    }

    const setUserDetails = async () => {
        const { data: userProfile = null } = await API.GetProfile();
        if (userProfile) {
            localStorage.setItem('userDetails', JSON.stringify(userProfile));
        }
    }

    useEffect(() => {
        dispatch(serVersion(window.location.pathname.includes('v1') ? 'v1' : 'v0'));
        const userDetails = JSON.parse(localStorage.getItem('userDetails') as string);
        if (!userDetails) {
            setUserDetails();
        }
        initConnection();
    }, []);

    const showNoCreditPopUp = () => {
        const app = localStorage.getItem('app') || 'tatva_ai';
        if (app === 'zymedelite') {
            Modal.confirm({
                title: 'Credit Expired',
                // content: 'Your credits have been exhausted. Please contact the administrator to add more credits in order to continue using Tatva AI.',
                content: 'Your credits have been exhausted. It will be credited soon.',
                centered: true,
                onOk() { },
                okText: 'Okay',
                // cancelButtonProps: { style: { display: 'none' } },
                okButtonProps: {
                    style: {
                        backgroundImage: 'linear-gradient(135deg, #4B22E5, #A553EE)',
                        borderColor: 'transparent'
                    },
                }
            });
        } else {
            Modal.confirm({
                title: 'Credit Expired',
                content: 'Please subscribe to continue using Tatva AI.',
                centered: true,
                onOk() { navigate('/my-subscription'); },
                okText: 'Upgrade to pro',
                // cancelButtonProps: { style: { display: 'none' } },
                okButtonProps: {
                    style: {
                        backgroundImage: 'linear-gradient(135deg, #4B22E5, #A553EE)',
                        borderColor: 'transparent'
                    },
                }
            });
        }
    }

    const onPressStartStream = async (query: string = '') => {
        try {
            if ((!textQuery && !query) || openAIResponse.some(obj => obj.loader === true)) {
                if (!textQuery && !query) console.log('No query', textQuery, query);
                if (openAIResponse.some(obj => obj.loader === true)) console.log('Loading...');
                return;
            }
            dispatch(setLoading(true));
            if (!socketConnection) {
                console.log('No socket connection found!');
                await initConnection();
                return;
            }
            const credit = await checkForCredits();
            if (!credit || credit <= 0) {
                showNoCreditPopUp();
                dispatch(setLoading(false));
                return;
            }
            let conversation_id = '';
            let followup = 0;
            if (openAIResponse.length) {
                let foundValidResponse = false;
                for (let i = openAIResponse.length - 1; i >= 0; i--) {
                    if (openAIResponse[i].status !== 'error') {
                        conversation_id = openAIResponse[i].conversation_id as string;
                        followup = 1;
                        foundValidResponse = true;
                        break;
                    }
                }
                if (!foundValidResponse) {
                    conversation_id = uuidv4();
                }
            } else {
                conversation_id = uuidv4();
            }
            socketConnection.send(JSON.stringify({ question: query ? query : textQuery, followup: followup, conversation_id }));
            const openAIResponses = [...openAIResponse];
            openAIResponses.push({ query: query ? query : textQuery, answer: '', sources: null, loader: true, conversation_id, loading_text: 'Collecting documents... Please wait.' });
            dispatch(setOpenAIResponse([...openAIResponses]));
            dispatch(setTextQuery(''));
        } catch (error) {
            console.log(error);
        }
    };

    const onPressSuggetion = (query: string) => {
        try {
            dispatch(setTextQuery(query));
            onPressStartStream(query);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (!threadFocus) return;
        dispatch(setOpenAIResponse([]));
        dispatch(setOpenAIPDFResponse([]));
        dispatch(setPdfData(null));
        dispatch(setThreadFocus(false));
    }, [threadFocus]);

    return (
        <div className={styles.chat_wrapper}>
            {!openAIResponse.length
                ? <LandingComponent onPressStartStream={onPressStartStream} onPressSuggetion={onPressSuggetion} />
                : <ConversationComponent openAIResponse={openAIResponse} onPressStartStream={onPressStartStream} setOpenAIResponse={setOpenAIResponse} />
            }
        </div>
    );
}

export default ChatWrapper;